import { z } from 'zod'

export type FlowchartAnswer = string | number | boolean

const BooleanTransitionsSchema = z.object({
  yes: z.string(),
  no: z.string(),
})

const SingleTransitionSchema = z.object({
  next: z.string(),
})

const TerminalTransitionSchema = z.object({}).strict()

export const NodeTransitionsSchema = z.union([
  BooleanTransitionsSchema,
  SingleTransitionSchema,
  TerminalTransitionSchema,
])

export type BooleanTransitions = z.infer<typeof BooleanTransitionsSchema>
export type SingleTransition = z.infer<typeof SingleTransitionSchema>
export type TerminalTransition = z.infer<typeof TerminalTransitionSchema>
export type NodeTransitions = z.infer<typeof NodeTransitionsSchema>

export const FlowchartSectionSchema = z.object({
  id: z.string(),
  type: z.literal('flowchart'),
  title: z.string().optional(),
  fieldId: z.string().optional(),
  startNode: z.string(),
  detailsId: z.string().optional(),
  nodes: z.record(z.string(), NodeTransitionsSchema),
})

export type FlowchartSection = z.infer<typeof FlowchartSectionSchema>

/**
 * Gets the field ID where flowchart result should be stored
 */
export function getFlowchartFieldId(section: FlowchartSection): string {
  return section.fieldId || section.id
}

export function isBooleanTransitions(
  transitions: NodeTransitions
): transitions is BooleanTransitions {
  return 'yes' in transitions && 'no' in transitions
}

export function isSingleTransition(
  transitions: NodeTransitions
): transitions is SingleTransition {
  return 'next' in transitions
}

export function isTerminalTransition(
  transitions: NodeTransitions
): transitions is TerminalTransition {
  return (
    !('yes' in transitions) &&
    !('no' in transitions) &&
    !('next' in transitions)
  )
}

export interface FlowchartState {
  currentNodeId: string
  answers: Record<string, FlowchartAnswer>
  history: string[]
  collectedFlags: string[]
  isComplete: boolean
  result: unknown | null
}

export interface FlowchartResult {
  result: unknown
  flags: string[]
  answers: Record<string, FlowchartAnswer>
}

export interface FlowchartDetails {
  result: unknown
  flags: string[]
  answers: Record<string, FlowchartAnswer>
  path: string[]
}
